import { motion } from "framer-motion";
import { scrollToSection } from "../../lib/scroll";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7 },
};

const capabilities = [
  ["Governance Gap Analysis", "Identify where stated commitments, decision-making authority and institutional practice fail to align."],
  ["Indigenous Data Sovereignty", "Embed authority, permission, control, benefit and accountability in the governance of First Nations information."],
  ["Cultural Governance Assurance", "Independent First Nations-led oversight of whether agreed protections remain operational throughout delivery."],
  ["Lore-Led Architecture Advisory", "Governance environments grounded in First Nations authority, continuity, responsibility and local decision-making."],
];

export default function Method() {
  return (
    <section id="method" data-testid="home-method" className="py-24 md:py-32 bg-surface1 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">What Bigibila has developed</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-8 max-w-4xl">
            Bigibila Governance Archaeology™
          </h2>
          <p className="text-stone2 leading-relaxed max-w-3xl mb-6">
            Bigibila Governance Archaeology is a sovereign Yuwaalaraay–Gamilaraay
            intellectual asset developed by Liana Pitt and held within the
            Bigibila enterprise. It examines how authority, responsibility,
            evidence and institutional power operate across fragmented systems.
          </p>
          <p className="text-stone2 leading-relaxed max-w-3xl mb-10">
            It supports organisations and First Nations communities to identify
            governance gaps, cultural risks, accountability failures and
            pathways for reconstruction.
          </p>
          <button type="button" onClick={() => scrollToSection("rigour")} data-testid="home-method-link" className="font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 border border-line text-bone hover:border-ember hover:text-ember transition-colors duration-300 inline-block">
            Research rigour
          </button>
        </motion.div>

        <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="mt-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mb-6">Capabilities</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line/40 border border-line/40">
            {capabilities.map(([title, desc], i) => (
              <div key={title} data-testid={`capability-${i}`} className="bg-ink p-6 hover:bg-surface2 transition-colors duration-300">
                <span className="font-mono text-xs text-ember block mb-2">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-lg text-bone leading-snug block mb-3">{title}</span>
                <p className="text-sm text-stone2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <p data-testid="home-boundary-statement" className="text-bone leading-relaxed max-w-4xl border-l-2 border-ember pl-5 mt-10">
            Bigibila's clients receive analysis, findings, recommendations and approved implementation outputs. Bigibila's codebook, decision rules, scoring architecture and internal analytical machinery remain exclusively within Bigibila.
          </p>
          <button type="button" onClick={() => scrollToSection("services")} data-testid="home-outcomes-services-link" className="font-mono text-xs uppercase tracking-[0.2em] text-ember hover:text-emberDeep transition-colors duration-300 inline-block mt-8">
            Explore Bigibila Services →
          </button>
        </motion.div>

        <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.15 }} className="mt-16 border border-line bg-ink p-8 md:p-10 flex flex-col md:flex-row items-center gap-10">
          <img
            src="/governance-before-data-wheel.png"
            alt="The Bigibila Governance before Data wheel — Country, Lore/Dreaming, Language, Kinship & Totems, Customs & Practice"
            data-testid="method-framework-image"
            className="w-full max-w-xs md:max-w-sm object-contain"
          />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre mb-3">The method, visualised</p>
            <h3 className="font-serif text-2xl md:text-3xl mb-4">Governance before data.</h3>
            <p className="text-stone2 text-sm leading-relaxed max-w-md">
              Country, Lore &amp; Dreaming, Language, Kinship &amp; Totems, and
              Customs &amp; Practice — the Bigibila framework places cultural
              authority and governance ahead of any data or systems work. This
              is the proposition the methodology is built on — the detailed
              instruments behind it remain confidential to Bigibila.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
